import type { ReactNode } from 'react';
import { Logo } from './Logo.tsx';

/** Large left-aligned page title (`display-lg`, iOS style) with an optional line and actions. */
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="display-lg text-ink">{title}</h1>
        {description ? <p className="callout mt-1 text-ink-muted">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </header>
  );
}

/** A card: surface-raised, radius-lg, hairline ring (brand/layout.md "Details panel"). */
export function Card({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="rounded-lg bg-surface-raised p-4 ring-1 ring-hairline">
      {title ? <h2 className="headline mb-3 text-ink">{title}</h2> : null}
      {children}
    </section>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="rounded-lg bg-surface px-4 py-8 text-center">
      <p className="headline text-ink">{title}</p>
      {children ? <div className="callout mt-1 text-ink-muted">{children}</div> : null}
    </div>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div role="status" className="callout flex items-center gap-2 py-6 text-ink-muted">
      <span
        className="size-4 animate-spin rounded-pill border-2 border-control-border border-t-transparent"
        aria-hidden
      />
      {label}…
    </div>
  );
}

/** Centered single-column page for signed-out and gate screens. */
export function CenteredPage({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-full items-start justify-center px-4 py-10 sm:items-center">
      <div className="flex w-full max-w-sm flex-col gap-6">{children}</div>
    </main>
  );
}

/** The wordmark on signed-out and gate screens. */
export function Brand() {
  return <Logo width={144} />;
}
